import * as THREE from 'three';

// GCode parser for visualization
export interface GCodeCommand {
  type: string;
  params: Record<string, number>;
  line: string;
  lineNumber: number;
}

export interface GCodeToolPath {
  path: THREE.Vector3[];
  type: 'travel' | 'extrude';
  z: number;
}

export class GCodeParser {
  private position: THREE.Vector3;
  private lastPosition: THREE.Vector3;
  private extruderPosition: number;
  private lastExtruderPosition: number;
  private toolPaths: GCodeToolPath[];
  private scale: number;
  private bounds: {
    min: THREE.Vector3;
    max: THREE.Vector3;
  };
  // Add properties for bed dimensions
  private bedSizeX: number | null;
  private bedSizeY: number | null;

  constructor() {
    this.position = new THREE.Vector3(0, 0, 0);
    this.lastPosition = new THREE.Vector3(0, 0, 0);
    this.extruderPosition = 0;
    this.lastExtruderPosition = 0;
    this.toolPaths = [];
    this.scale = 1;
    this.bounds = {
      min: new THREE.Vector3(Infinity, Infinity, Infinity),
      max: new THREE.Vector3(-Infinity, -Infinity, -Infinity)
    };
    // Initialize bed dimensions
    this.bedSizeX = null;
    this.bedSizeY = null;
  }

  public parse(gcode: string): GCodeToolPath[] {
    this.reset();
    
    const lines = gcode.split('\n');

    // First pass - parse commands
    const commands: GCodeCommand[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip comments and empty lines (but check comments for metadata)
      if (!line) continue;
      
      if (line.startsWith(';')) {
          this.parseMetadata(line);
          continue; // Don't process comments further
      }
      
      // Remove inline comments
      const cleanLine = line.split(';')[0].trim();
      
      // Parse command
      const command = this.parseCommand(cleanLine, i);
      if (command) {
        commands.push(command);
      }
    }
    
    // Second pass - generate toolpaths
    for (const command of commands) {
      this.processCommand(command);
    }
    
    // Normalize the model to sit on the print bed
    this.normalizeHeight();
    
    // Filter out very short segments that might just be noise
    this.filterShortSegments(0.01);
    
    console.log('[DEBUG] Parsed G-code bounds:', this.bounds);
    console.log('[DEBUG] Generated', this.toolPaths.length, 'toolpaths');
    
    return this.toolPaths;
  }
  
  private reset(): void {
    this.position = new THREE.Vector3(0, 0, 0);
    this.lastPosition = new THREE.Vector3(0, 0, 0);
    this.extruderPosition = 0;
    this.lastExtruderPosition = 0;
    this.toolPaths = [];
    this.bounds = {
      min: new THREE.Vector3(Infinity, Infinity, Infinity),
      max: new THREE.Vector3(-Infinity, -Infinity, -Infinity)
    };
    // Reset bed dimensions
    this.bedSizeX = null;
    this.bedSizeY = null;
  }
  
  private parseMetadata(line: string): void {
    // Example PrusaSlicer format: ; bed_shape = 0x0,180x0,180x180,0x180
    const bedShapeMatch = line.match(/;\s*bed_shape\s*=\s*(.+)/);
    if (bedShapeMatch) {
        console.log(`[GCODE PARSER DEBUG] Found bed_shape line: ${line}`);
        try {
            const coordsStr = bedShapeMatch[1];
            console.log(`[GCODE PARSER DEBUG] Parsing coordinates: ${coordsStr}`);
            const coords = coordsStr.split(',').map(point => {
                const [x, y] = point.split('x').map(n => parseFloat(n));
                return { x, y };
            });
            
            console.log(`[GCODE PARSER DEBUG] Parsed coordinates:`, coords);
            
            if (coords.length >= 4) {
                // Calculate bed dimensions from all corner coordinates
                const xValues = coords.map(c => c.x);
                const yValues = coords.map(c => c.y);
                this.bedSizeX = Math.max(...xValues) - Math.min(...xValues);
                this.bedSizeY = Math.max(...yValues) - Math.min(...yValues);
                console.log(`[GCODE PARSER DEBUG] ✅ Calculated bed dimensions from ${coords.length} points: X=${this.bedSizeX}, Y=${this.bedSizeY}`);
                return; // Found it
            } else {
                console.warn(`[GCODE PARSER DEBUG] ❌ Not enough coordinates (${coords.length}), need at least 4`);
            }
        } catch (error) {
            console.error(`[GCODE PARSER DEBUG] ❌ Error parsing bed_shape: ${error}`);
        }
    }

    // Check for direct bed dimensions format (our backend injects this)
    const bedDimensionsMatch = line.match(/;\s*bed_dimensions\s*=\s*([\d.]+)x([\d.]+)/);
    if (bedDimensionsMatch) {
        console.log(`[GCODE PARSER DEBUG] Found bed_dimensions line: ${line}`);
        this.bedSizeX = parseFloat(bedDimensionsMatch[1]);
        this.bedSizeY = parseFloat(bedDimensionsMatch[2]);
        console.log(`[GCODE PARSER DEBUG] ✅ Parsed bed dimensions: X=${this.bedSizeX}, Y=${this.bedSizeY}`);
        return;
    }

    // Example Cura format: ;MAXX:120
    // Example Cura format: ;MAXY:120
    // Note: Cura often defines size relative to center (0,0)
    const maxXMatch = line.match(/;\s*MAXX\s*:\s*([\d.]+)/);
    if (maxXMatch) {
        // Assuming origin is center, MAX is half the size
        // If we already parsed Y, use it, otherwise wait.
        const sizeX = parseFloat(maxXMatch[1]) * 2;
        if (!this.bedSizeX) this.bedSizeX = sizeX;
        console.log(`[DEBUG] Parsed MAX X: ${sizeX}`);
        return;
    }
    const maxYMatch = line.match(/;\s*MAXY\s*:\s*([\d.]+)/);
    if (maxYMatch) {
        const sizeY = parseFloat(maxYMatch[1]) * 2;
        if (!this.bedSizeY) this.bedSizeY = sizeY;
        console.log(`[DEBUG] Parsed MAX Y: ${sizeY}`);
        return;
    }
    
    // Add more patterns here as needed for other slicers...
  }
  
  private parseCommand(line: string, lineNumber: number): GCodeCommand | null {
    // Match the command type (G0, G1, etc.)
    const commandMatch = line.match(/^([GM][0-9]+)/);
    
    if (!commandMatch) return null;
    
    const type = commandMatch[1];
    const params: Record<string, number> = {};
    
    // Extract parameters (X, Y, Z, E, F)
    const paramMatches = line.matchAll(/([XYZEF])(-?[0-9.]+)/g);
    
    for (const match of paramMatches) {
      const [, param, value] = match;
      params[param] = parseFloat(value);
    }
    
    return { type, params, line, lineNumber };
  }
  
  private processCommand(command: GCodeCommand): void {
    // Only process movement commands (G0, G1)
    if (command.type !== 'G0' && command.type !== 'G1') return;
    
    // Update position based on parameters
    this.lastPosition.copy(this.position);
    this.lastExtruderPosition = this.extruderPosition;
    
    if ('X' in command.params) this.position.x = command.params.X;
    if ('Y' in command.params) this.position.y = command.params.Y;
    if ('Z' in command.params) this.position.z = command.params.Z;
    if ('E' in command.params) this.extruderPosition = command.params.E;
    
    // Skip if no spatial movement
    if (this.lastPosition.equals(this.position)) return;
    
    // Update bounds
    this.bounds.min.min(this.position);
    this.bounds.max.max(this.position);
    
    // Determine if this is an extrusion move (E value increased)
    const isExtruding = this.extruderPosition > this.lastExtruderPosition;
    
    // G0 is typically a travel move regardless of E value
    // G1 with increasing E is extrusion, otherwise travel
    const pathType = (command.type === 'G0' || !isExtruding) ? 'travel' : 'extrude';
    
    // Check if we can add to the last path (same type and layer height)
    const lastPath = this.toolPaths.length > 0 ? this.toolPaths[this.toolPaths.length - 1] : null;
    
    if (lastPath && 
        lastPath.type === pathType && 
        Math.abs(lastPath.z - this.position.z) < 0.01) {
      // Add to existing path - avoid adding duplicate points
      if (!lastPath.path[lastPath.path.length - 1].equals(this.position)) {
        lastPath.path.push(this.position.clone());
      }
    } else {
      // Create new path starting from last position
      this.toolPaths.push({
        path: [this.lastPosition.clone(), this.position.clone()],
        type: pathType,
        z: this.position.z
      });
    }
  }
  
  private normalizeHeight(): void {
    // If no paths, nothing to normalize
    if (this.toolPaths.length === 0) return;
    
    // Find the minimum Z value to use as our reference for the build plate
    const minZ = this.bounds.min.z;
    
    // Only adjust Z height to make bottom at Z=0, preserve X/Y coordinates
    for (const path of this.toolPaths) {
      for (const point of path.path) {
        point.z -= minZ;
        // No longer modifying X and Y to center
      }
      // Update path z value
      path.z -= minZ;
    }
    
    // Update bounds for Z only
    const sizeZ = this.bounds.max.z - this.bounds.min.z;
    
    // Keep original X and Y bounds
    // Only update Z bounds
    this.bounds.min.z = 0;
    this.bounds.max.z = sizeZ;
    
    console.log('[DEBUG] After height normalization:');
    console.log('[DEBUG] X range:', this.bounds.min.x, 'to', this.bounds.max.x);
    console.log('[DEBUG] Y range:', this.bounds.min.y, 'to', this.bounds.max.y);
    console.log('[DEBUG] Z range:', this.bounds.min.z, 'to', this.bounds.max.z);
  }
  
  private filterShortSegments(minLength: number): void {
    // Remove very short segments that might be artifacts
    this.toolPaths = this.toolPaths.filter(path => {
      if (path.path.length < 2) return false;
      
      // Calculate path length
      let totalLength = 0;
      for (let i = 1; i < path.path.length; i++) {
        totalLength += path.path[i].distanceTo(path.path[i - 1]);
      }
      
      return totalLength >= minLength;
    });
  }
  
  public getBounds(): { min: THREE.Vector3, max: THREE.Vector3 } {
    return this.bounds;
  }
  
  // Add getter for bed dimensions
  public getBedDimensions(): { x: number | null, y: number | null } {
      return { x: this.bedSizeX, y: this.bedSizeY };
  }
}

export function createGCodeGeometry(toolPaths: GCodeToolPath[]): { 
  extrudeGeometry: THREE.BufferGeometry, 
  travelGeometry: THREE.BufferGeometry 
} {
  // Separate points by type
  const extrudePaths: THREE.Vector3[][] = [];
  const travelPaths: THREE.Vector3[][] = [];
  
  // Group paths by type
  for (const path of toolPaths) {
    if (path.path.length < 2) continue;
    
    if (path.type === 'extrude') {
      extrudePaths.push(path.path);
    } else {
      travelPaths.push(path.path);
    }
  }
  
  // Create line segments for each path type
  const extrudeLines: THREE.Vector3[] = [];
  const travelLines: THREE.Vector3[] = [];
  
  // Process extrude paths - create line segments
  for (const path of extrudePaths) {
    for (let i = 0; i < path.length - 1; i++) {
      extrudeLines.push(path[i], path[i + 1]);
    }
  }
  
  // Process travel paths - create line segments
  for (const path of travelPaths) {
    for (let i = 0; i < path.length - 1; i++) {
      travelLines.push(path[i], path[i + 1]);
    }
  }
  
  // Create geometries
  const extrudeGeometry = new THREE.BufferGeometry().setFromPoints(extrudeLines);
  const travelGeometry = new THREE.BufferGeometry().setFromPoints(travelLines);
  
  console.log('[DEBUG] Created geometry with', extrudeLines.length / 2, 'extrusion lines and', 
              travelLines.length / 2, 'travel lines');
  
  return { extrudeGeometry, travelGeometry };
} 